// Auditoria de Ajustes de Estoque: todos os lançamentos manuais do kardex
// (Acerto Manual e Balanço — inclusive o do inventário e o ajuste direto de
// saldo da matéria-prima) num período, com quantidade, saldos e responsável.
// admin, finance e logistics_operator (reports.stock_adjustments_audit). Regras: RN-EST-76.

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowDownCircle, ArrowUp, ArrowUpCircle, ArrowUpDown, ClipboardList, Download, FileText, Loader2, Scale, Search, Users } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { Badge } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { PermissionGuard } from '@components/PermissionGuard';
import { SimpleTable, TableCell, TableHeader, TableRow } from '@components/Table';
import { formatDateTimeBR } from '@pages/financial/financialUtils';
import { stockAdjustmentsAuditService } from '../../services/stockAdjustmentsAuditService';
import type { StockAdjustmentAuditResponse, StockAdjustmentAuditRow, StockAdjustmentAuditSort } from '../../types/stockAdjustmentsAudit';

const PAGE_SIZE = 50;

const fmtQty = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
const fmtInt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR'));
/** "+3,5" / "−2" / "0" — variação do saldo com sinal. */
const fmtDelta = (v: number | null | undefined) => {
  if (v === null || v === undefined) return '—';
  const s = Math.abs(v).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  return v > 0 ? `+${s}` : v < 0 ? `−${s}` : s;
};

const pad = (n: number) => String(n).padStart(2, '0');
const localISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstOfMonthISO = () => {
  const d = new Date();
  return localISO(new Date(d.getFullYear(), d.getMonth(), 1));
};
const todayISO = () => localISO(new Date());

const SORT_OPTIONS: { value: StockAdjustmentAuditSort; label: string }[] = [
  { value: 'date_desc', label: 'Data (mais recente)' },
  { value: 'date_asc', label: 'Data (mais antiga)' },
  { value: 'delta_desc', label: 'Variação (maior → menor)' },
  { value: 'delta_asc', label: 'Variação (menor → maior)' },
  { value: 'abs_delta_desc', label: 'Maior variação (em módulo)' },
];

/** Clique no cabeçalho: alterna entre os dois sentidos da coluna, começando pelo informado. */
const nextSort = (current: StockAdjustmentAuditSort, first: StockAdjustmentAuditSort, second: StockAdjustmentAuditSort) =>
  current === first ? second : first;

const SortIcon: React.FC<{ active: 'asc' | 'desc' | null }> = ({ active }) =>
  active === 'desc' ? <ArrowDown size={12} /> : active === 'asc' ? <ArrowUp size={12} /> : <ArrowUpDown size={12} className="text-slate-300" />;

const ORIGIN_LABEL: Record<number, string> = { 2: 'Acerto Manual', 3: 'Balanço' };
const TYPE_LABEL: Record<number, string> = { 0: 'Entrada', 1: 'Saída', 2: 'Saldo' };
const typeVariant = (t: number): 'success' | 'danger' | 'info' => (t === 0 ? 'success' : t === 1 ? 'danger' : 'info');

/** Texto da observação: título do inventário na frente, quando o balanço veio da conferência. */
const rowNotes = (row: Pick<StockAdjustmentAuditRow, 'notes' | 'inventory_session_title'>): string => {
  const parts: string[] = [];
  if (row.inventory_session_title) parts.push(`Inventário: ${row.inventory_session_title}`);
  if (row.notes) parts.push(row.notes);
  return parts.join(' — ');
};

const SummaryCard: React.FC<{ icon: React.ReactNode; label: string; value: string; accent: string; sub?: string }> = ({
  icon,
  label,
  value,
  accent,
  sub,
}) => (
  <Card padding="sm">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        {sub && <p className="text-[11px] text-slate-400 truncate">{sub}</p>}
      </div>
    </div>
  </Card>
);

export const StockAdjustmentsAuditView: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [originType, setOriginType] = useState<'' | '2' | '3'>('');
  const [item, setItem] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  // Padrão ligado: balanço de inventário que confirmou a mesma quantidade é conferência, não ajuste.
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [sort, setSort] = useState<StockAdjustmentAuditSort>('date_desc');
  const [page, setPage] = useState(1);

  const [data, setData] = useState<StockAdjustmentAuditResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Responsáveis conhecidos (do resumo da última consulta) para o filtro.
  const [knownUsers, setKnownUsers] = useState<string[]>([]);

  const filters = useCallback(
    () => ({
      date_from: dateFrom,
      date_to: dateTo,
      origin_type: originType ? (Number(originType) as 2 | 3) : undefined,
      item: item || undefined,
      created_by: createdBy || undefined,
      only_changed: onlyChanged,
      sort,
    }),
    [dateFrom, dateTo, originType, item, createdBy, onlyChanged, sort],
  );

  const load = useCallback(
    async (targetPage: number) => {
      if (!dateFrom || !dateTo) {
        setError('Informe o período');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const resp = await stockAdjustmentsAuditService.getReport({ ...filters(), page: targetPage, limit: PAGE_SIZE });
        setData(resp);
        setPage(targetPage);
        if (!createdBy) setKnownUsers(resp.summary.by_user.map((u) => u.created_by).filter(Boolean));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar a auditoria');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [dateFrom, dateTo, filters, createdBy],
  );

  useEffect(() => {
    load(1);
    // na abertura e ao trocar a ordenação (volta para a página 1) — os demais filtros consultam pelo botão
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const handleExport = async (kind: 'excel' | 'pdf') => {
    setExporting(kind);
    setError(null);
    try {
      if (kind === 'excel') await stockAdjustmentsAuditService.downloadExcel(filters());
      else await stockAdjustmentsAuditService.downloadPdf(filters());
    } catch (err) {
      setError(err instanceof Error ? err.message : `Erro ao gerar ${kind === 'pdf' ? 'PDF' : 'Excel'}`);
    } finally {
      setExporting(null);
    }
  };

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const totalPages = data?.pagination.total_pages ?? 1;
  const topUser = summary?.by_user[0];

  return (
    <PermissionGuard permission="reports.stock_adjustments_audit">
      <div className="space-y-6">
        <p className="text-sm text-slate-500">
          Lançamentos manuais do kardex (acertos de saldo e balanços, inclusive os da conferência de inventário) no período,
          com o responsável. Saídas e entradas automáticas (pedidos, recebimentos, produção) não entram.
        </p>

        <Card>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">De</label>
                <DateInputBR className="input w-full" value={dateFrom} onChange={setDateFrom} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Até</label>
                <DateInputBR className="input w-full" value={dateTo} onChange={setDateTo} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Origem</label>
                <select className="input w-full" value={originType} onChange={(e) => setOriginType(e.target.value as '' | '2' | '3')}>
                  <option value="">Todas</option>
                  <option value="2">Acerto Manual</option>
                  <option value="3">Balanço / Inventário</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Item (SKU, código ou descrição)</label>
                <input
                  className="input w-full"
                  value={item}
                  onChange={(e) => setItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load(1)}
                  placeholder="Ex.: CAB-001"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Responsável</label>
                <select className="input w-full" value={createdBy} onChange={(e) => setCreatedBy(e.target.value)}>
                  <option value="">Todos</option>
                  {knownUsers.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => load(1)} disabled={loading} icon={loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}>
                  Consultar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport('excel')}
                  disabled={exporting !== null || !data || summary?.count === 0}
                  icon={exporting === 'excel' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                >
                  Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport('pdf')}
                  disabled={exporting !== null || !data || summary?.count === 0}
                  icon={exporting === 'pdf' ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                >
                  PDF
                </Button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                Ordenar por
                <select className="input py-1 text-xs" value={sort} onChange={(e) => setSort(e.target.value as StockAdjustmentAuditSort)}>
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyChanged}
                  onChange={(e) => setOnlyChanged(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Somente lançamentos com variação de saldo (esconde balanços que só confirmaram a quantidade, como os da
                conferência de inventário)
              </label>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SummaryCard icon={<ClipboardList size={18} className="text-slate-700" />} label="Lançamentos" value={fmtInt(summary.count)} accent="bg-slate-100" />
            <SummaryCard
              icon={<ArrowUpCircle size={18} className="text-emerald-700" />}
              label="Acertos de entrada"
              value={fmtInt(summary.entries)}
              sub={`${fmtQty(summary.entry_qty)} un`}
              accent="bg-emerald-100"
            />
            <SummaryCard
              icon={<ArrowDownCircle size={18} className="text-red-700" />}
              label="Acertos de saída"
              value={fmtInt(summary.exits)}
              sub={`${fmtQty(summary.exit_qty)} un`}
              accent="bg-red-100"
            />
            <SummaryCard icon={<Scale size={18} className="text-blue-700" />} label="Balanços" value={fmtInt(summary.balances)} accent="bg-blue-100" />
            <SummaryCard
              icon={<Users size={18} className="text-amber-700" />}
              label="Responsáveis"
              value={fmtInt(summary.by_user.length)}
              sub={topUser ? `${topUser.created_by} (${fmtInt(topUser.count)})` : undefined}
              accent="bg-amber-100"
            />
          </div>
        )}

        <Card padding="none">
          <CardHeader title="Lançamentos" subtitle={data ? `${fmtInt(data.pagination.total_items)} no período` : undefined} />
          <SimpleTable>
            <TableHeader>
              <tr>
                <th className="table-header th">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-slate-900"
                    onClick={() => setSort(nextSort(sort, 'date_desc', 'date_asc'))}
                    title="Ordenar pela data"
                  >
                    Data/hora
                    <SortIcon active={sort === 'date_desc' ? 'desc' : sort === 'date_asc' ? 'asc' : null} />
                  </button>
                </th>
                <th className="table-header th">Item</th>
                <th className="table-header th">Origem</th>
                <th className="table-header th">Tipo</th>
                <th className="table-header th text-right">Quantidade</th>
                <th className="table-header th text-right">Saldo anterior → novo</th>
                <th className="table-header th text-right">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-slate-900"
                    onClick={() => setSort(nextSort(sort, 'delta_desc', 'delta_asc'))}
                    title="Ordenar pela variação (maior → menor / menor → maior)"
                  >
                    Variação
                    <SortIcon active={sort === 'delta_desc' || sort === 'abs_delta_desc' ? 'desc' : sort === 'delta_asc' ? 'asc' : null} />
                  </button>
                </th>
                <th className="table-header th">Responsável</th>
                <th className="table-header th">Observação</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <span className="text-sm text-slate-500">Carregando…</span>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10">
                    <span className="text-sm text-slate-500">Nenhum lançamento manual no período.</span>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow key={row.stock_movement_id}>
                    <TableCell className="whitespace-nowrap text-xs">{formatDateTimeBR(row.movement_date)}</TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{row.sku || '—'}</span>
                      <span className="block text-xs text-slate-600">{row.description ?? '—'}</span>
                      {row.item_kind === 'raw_material' && (
                        <span className="text-[10px] text-slate-400">matéria-prima</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{ORIGIN_LABEL[row.origin_type] ?? row.origin_type}</TableCell>
                    <TableCell>
                      <Badge variant={typeVariant(row.movement_type)} size="sm">
                        {TYPE_LABEL[row.movement_type] ?? row.movement_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{fmtQty(row.quantity)}</TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">
                      {fmtQty(row.previous_balance)} <span className="text-slate-400">→</span> {fmtQty(row.new_balance)}
                    </TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">
                      <span
                        className={
                          row.balance_delta != null && row.balance_delta > 0
                            ? 'text-emerald-700 font-medium'
                            : row.balance_delta != null && row.balance_delta < 0
                              ? 'text-red-700 font-medium'
                              : 'text-slate-400'
                        }
                      >
                        {fmtDelta(row.balance_delta)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{row.created_by ?? '—'}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-md">{rowNotes(row) || '—'}</TableCell>
                  </TableRow>
                ))}
            </tbody>
          </SimpleTable>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
              <span className="text-slate-500">
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => load(page + 1)}>
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </PermissionGuard>
  );
};

export default StockAdjustmentsAuditView;
